import { z } from "zod";
import { SourceImageDto } from "./source-image.ts";

export const AnimatedImageType = z.enum(["UPLOADED", "GENERATED"]);

export const CameraMovement = z.enum([
  "PUSH_IN",
  "PULL_OUT",
  "ROTATE",
  "TRUCK_LEFT",
  "TRUCK_RIGHT",
  "DRONE_UP",
  "STATIC_SHOT",
  "PEDESTAL_UP",
  "PEDESTAL_DOWN",
]);

export const AnimationStyle = z.enum([
  "SUNSET",
  "TWILIGHT",
  "NIGHT",
  "SUNRISE",
  "MIDDAY",
  "AFTERNOON",
  "CEILING_FANS",
  "WATER",
  "FOG",
  "HELICOPTER",
  "HOUSE_DROP",
  "HOUSE_JUMP",
  "PENCIL_DRAWING",
  "FURNITURE_REVEAL",
  "FURNITURE_REVEAL_SE",
  "FIREPLACE",
  "DECLUTTER",
  "TV",
  "TIMELAPSE_DAY_TO_NIGHT",
  "TIMELAPSE_NIGHT_TO_DAY",
  "TIMELAPSE_SUNRISE",
  "TIMELAPSE_SUNSET",
  "SPEED_RAMP",
  "STEADY_GLIDE",
  "AERIAL_TRANSITION",
  "MAGAZINE",
  "NEWSPAPER",
]);

export const AnimatedImagePromptType = z.string();

export const AnimatedImageStatus = z.enum([
  "INITIAL",
  "PROCESSING",
  "POST_PROCESSING",
  "COMPLETED",
  "FAILED",
]);
export const AnimatedImageProviderType = z.enum([
  "fal-ai/kling-1.6",
  "amplifiles/perspective-1.0",
  "amplifiles/perspective-2.0",
  "amplifiles/perspective-2.1",
  "amplifiles/perspective-3.0",
  "amplifiles/perspective-3.1",
  "amplifiles/perspective-4.0",
  "vast-ai/ltx-2b",
  "dummy/ffmpeg",
]);

export const AnimatedImageSmStatus = z.enum([
  "INITIAL",
  "PROCESSING",
  "POST_PROCESSING",
  "COMPLETED",
  "FAILED",
]);
export const AnimatedImageSmProviderType = z.enum([
  "amplifiles/slow-motion-1.0",
]);

export const AnimatedImageCustomInput = z.object({
  prompt: z.string(),
  duration: z.number(),
});

export const CreateAnimatedImageDto = z.object({
  timelineItemExternalId: z.string(),
  originVideoExternalId: z.string(),
  startImageExternalId: z.string(),
  endImageExternalId: z.string().optional(),
  promptType: AnimatedImagePromptType,
  provider: AnimatedImageProviderType,
  motionIntensity: z.number().optional(),
});

export const CreateCustomInputAnimatedImageDto = z.object({
  originVideoExternalId: z.string(),
  startImageExternalId: z.string(),
  endImageExternalId: z.string().optional(),
  provider: AnimatedImageProviderType,
  customInput: AnimatedImageCustomInput,
});

export const InitializeSlowMotionDto = z.object({
  animatedImageExternalId: z.string(),
  provider: AnimatedImageSmProviderType,
});

export const AnimatedImageDto = z.object({
  externalId: z.string(),
  createdAt: z.string(),
  status: AnimatedImageStatus,
  type: AnimatedImageType,
  hasAudio: z.boolean(),
  forwardSrc: z.string(),
  backwardSrc: z.string(),
  startImageExternalId: z.string(),
  endImageExternalId: z.string().nullable(),
  durationMs: z.number(),
  width: z.number(),
  height: z.number(),
  promptType: AnimatedImagePromptType.nullable(),
  provider: AnimatedImageProviderType.nullable(),
  smStatus: AnimatedImageSmStatus.nullable(),
  deletedAt: z.string().nullable(),
});

export const UploadAnimatedImageResponseDto = z.object({
  animatedImage: AnimatedImageDto,
  startSourceImage: SourceImageDto,
});

export const CreateAnimatedImageResponseDto = z.object({
  timelineItemExternalId: z.string(),
  animatedImage: AnimatedImageDto,
});

export const GetAnimatedImagesByExtIdsDto = z.object({
  externalIds: z.array(z.string()),
});

export const GetAnimatedImagesByExtIdsResponseDto = z.record(
  z.string(),
  AnimatedImageDto,
);

export const BulkDownloadAnimatedImagesDto = z.object({
  animatedImageExternalIds: z.array(z.string()),
});

export const GetQuickAnimationCreditsDto = z.object({
  sourceImageExternalIds: z.array(z.string()),
});

export const GetQuickAnimationCreditsResponseDto = z.object({
  credits: z.number(),
});

export const DeleteAnimatedImagesDto = z.object({
  externalIds: z.array(z.string()),
});

export const DeleteAnimatedImagesResponseDto = z.object({
  deletedCount: z.number(),
});

export const AnimatedImageWithThumbnailDto = AnimatedImageDto.extend({
  thumbnailSrc: z.string(),
});

export const GetAnimatedImagesByOriginVideoExternalIdResponseDto = z.array(
  AnimatedImageWithThumbnailDto,
);

export const GetAnimatedImagesQuery = z.object({
  pageSize: z.coerce.number(),
  cursorCreatedAt: z.string().optional(),
  cursorExternalId: z.string().optional(),
});

export const PagedAnimatedImagesResponse = z.object({
  animatedImages: z.array(AnimatedImageWithThumbnailDto),
  nextCursor: z
    .object({
      createdAt: z.string(),
      externalId: z.string(),
    })
    .optional(),
});

export type AnimatedImageType = z.infer<typeof AnimatedImageType>;
export type AnimatedImagePromptType = z.infer<typeof AnimatedImagePromptType>;
export type CameraMovement = z.infer<typeof CameraMovement>;
export type AnimationStyle = z.infer<typeof AnimationStyle>;
export type AnimatedImageStatus = z.infer<typeof AnimatedImageStatus>;
export type AnimatedImageProviderType = z.infer<
  typeof AnimatedImageProviderType
>;
export type AnimatedImageSmStatus = z.infer<typeof AnimatedImageSmStatus>;
export type AnimatedImageSmProviderType = z.infer<
  typeof AnimatedImageSmProviderType
>;
export type CreateAnimatedImageDto = z.infer<typeof CreateAnimatedImageDto>;
export type AnimatedImageCustomInput = z.infer<typeof AnimatedImageCustomInput>;
export type CreateCustomInputAnimatedImageDto = z.infer<
  typeof CreateCustomInputAnimatedImageDto
>;
export type InitializeSlowMotionDto = z.infer<typeof InitializeSlowMotionDto>;
export type AnimatedImageDto = z.infer<typeof AnimatedImageDto>;
export type UploadAnimatedImageResponseDto = z.infer<
  typeof UploadAnimatedImageResponseDto
>;
export type CreateAnimatedImageResponseDto = z.infer<
  typeof CreateAnimatedImageResponseDto
>;
export type GetAnimatedImagesByExtIdsDto = z.infer<
  typeof GetAnimatedImagesByExtIdsDto
>;
export type GetAnimatedImagesByExtIdsResponseDto = z.infer<
  typeof GetAnimatedImagesByExtIdsResponseDto
>;
export type BulkDownloadAnimatedImagesDto = z.infer<
  typeof BulkDownloadAnimatedImagesDto
>;
export type GetQuickAnimationCreditsDto = z.infer<
  typeof GetQuickAnimationCreditsDto
>;
export type GetQuickAnimationCreditsResponseDto = z.infer<
  typeof GetQuickAnimationCreditsResponseDto
>;
export type DeleteAnimatedImagesDto = z.infer<typeof DeleteAnimatedImagesDto>;
export type DeleteAnimatedImagesResponseDto = z.infer<
  typeof DeleteAnimatedImagesResponseDto
>;
export type AnimatedImageWithThumbnailDto = z.infer<
  typeof AnimatedImageWithThumbnailDto
>;
export type GetAnimatedImagesByOriginVideoExternalIdResponseDto = z.infer<
  typeof GetAnimatedImagesByOriginVideoExternalIdResponseDto
>;
export type GetAnimatedImagesQuery = z.infer<typeof GetAnimatedImagesQuery>;
export type PagedAnimatedImagesResponse = z.infer<
  typeof PagedAnimatedImagesResponse
>;
